import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '../api/types.ts';

/**
 * Who is signed in, and what they are allowed to do.
 *
 * The permissions come from the server with the session and are the same
 * strings the API checks. That matters: this is not a second, client-side
 * model of authority that can drift from the real one — it is the real one,
 * echoed, so the interface can stop offering an action before the customer
 * discovers by pressing it that their account cannot do it.
 *
 * It is a *courtesy*, never a control. Every one of these permissions is
 * enforced again on the server for every request, and nothing here can grant
 * anything. A support account with the console open can flip a flag in memory
 * and still be refused by the API, which is the only place the refusal counts.
 */

const SessionContext = createContext<Session | undefined>(undefined);

export function SessionProvider({
  session,
  children,
}: {
  session: Session;
  children: ReactNode;
}): ReactNode {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside a SessionProvider');
  return session;
}

/**
 * Whether this account holds a permission.
 *
 * An unknown permission string is `false`. A typo should hide a button, not
 * reveal one — the failure that costs nothing is the one to prefer when the
 * other costs a customer a 403 they cannot explain.
 */
export function useCan(permission: string): boolean {
  const session = useContext(SessionContext);
  return useMemo(() => session?.permissions.includes(permission) ?? false, [session, permission]);
}
